<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useWindowScroll, useWindowSize } from '@vueuse/core'
import { rem2Px } from '@/utils/rem'
import MessagePlain from '@/views/chat/components/message-plain.vue'
import MessageGuess from '@/views/chat/components/message-guess.vue'
import Watermark from '@/components/watermark.vue'
import StatementModal from '@/views/chat/components/statement-modal.vue'
import ArchiveModal from '@/views/chat/components/archive-modal.vue'
import { getFaqApi, getInventoryApi, getWelcomeApi, sendChatApi } from '@/api/chatApi'
import MessageTable from '@/views/chat/components/message-table.vue'
import MessageEcharts from '@/views/chat/components/message-echarts.vue'
import { sendDistributorApi } from '@/api/distributorApi'
import moment from 'moment'
import MessageMd from '@/views/chat/components/message-md.vue'
import type { IApiOptions, IResponseTableType, ResponseMessageType } from '@/types'
import { createRequestQueue, wait } from '@/utils/util'
import { useMessageStore } from '@/stores/message'
import { storeToRefs } from 'pinia'
import { showToast } from '@nutui/nutui'
import MessageWait from '@/views/chat/components/message-wait.vue'

const { requestQueue, working: messageLoading } = createRequestQueue(3, (rejectReason) => {
  switch (rejectReason) {
    case 'countLimitReject':
      showToast.text('您的小手太快了')
      break
  }
})
watch(messageLoading, (v) => {
  if (v) {
    handleScrollBottom()
  }
})
const loadingAnimationTimeMs = ref(1000)
const canvasRef = ref()
const archiveModalRef = ref<InstanceType<typeof ArchiveModal>>()
const { width, height } = useWindowSize()
const textMessage = ref('')
const { y } = useWindowScroll()
const isBottom = computed(() => {
  if (!y.value && document.body.offsetHeight <= window.innerHeight) {
    return true
  }
  return Math.abs(document.body.offsetHeight - window.innerHeight - y.value) < 10
})
const messageStore = useMessageStore()
const { isEmpty, hasHistory, messageList } = storeToRefs(messageStore)
const { getPrevMessage, messagePush: storeMessagePush, saveMessage } = messageStore
function handleSend() {
  const message = textMessage.value.trim()
  textMessage.value = ''
  sendMessage(message)
}

const waterContent = computed(() => {
  return sessionStorage.getItem('cwid') + ' ' + moment().format('YYYY-MM-DD')
})
const img = new Image()
onMounted(() => {
  img.onload = () => {
    renderHeader(canvasRef.value)
  }
  img.onerror = (e) => {
    console.error('err', e)
  }
  img.src = './images/pic-header.png'

  const top = Math.max(0, document.body.offsetHeight - window.innerHeight + 100)
  window.scrollTo(0, top)
})

// 顶部 header 渲染
const r = 1.147
const radius = ref(rem2Px(r))
function renderHeader(canvas: HTMLCanvasElement) {
  const dpRatio = window.devicePixelRatio
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  // 宽高比
  const imgWHRatio = img.naturalWidth / img.naturalHeight
  canvas.width = canvas.offsetWidth * dpRatio
  canvas.height = canvas.offsetHeight * dpRatio

  ctx.save()
  const r = radius.value * dpRatio
  ctx.beginPath()
  ctx.moveTo(0, canvas.height)
  ctx.arcTo(0, canvas.height - r, canvas.width - r, canvas.height - r, r)
  ctx.arcTo(canvas.width, canvas.height - r, canvas.width, canvas.height, r)
  ctx.lineTo(canvas.width, 0)
  ctx.lineTo(0, 0)
  ctx.closePath()
  ctx.clip()
  ctx.drawImage(img, 0, 0, canvas.width, canvas.width / imgWHRatio)
  ctx.restore()
}
watch(width, () => {
  radius.value = rem2Px(r)
  renderHeader(canvasRef.value)
})
// 顶部 header 渲染 end

function messagePush(messages: ResponseMessageType[]) {
  storeMessagePush(messages)
  handleScrollBottom()
}

// 经销商档案弹窗确定事件
async function onArchiveOk(data: IApiOptions['Distributor']) {
  const res = await requestQueue(() => sendDistributorApi(data))
  messagePush(res.responses)
}

async function onInventoryClick() {
  const res = await requestQueue(() => getInventoryApi())
  messagePush(res.responses)
}

async function onFaqClick() {
  const res = await requestQueue(() => getFaqApi())
  messagePush(res.responses)
}

async function sendMessage(message: string) {
  if (!message) return
  messageList.value.push({
    message,
    from: 'self',
    timestamp: +new Date(),
  })
  saveMessage()
  await nextTick()
  const top = Math.max(0, document.body.offsetHeight - window.innerHeight + 100)
  window.scrollBy({
    top,
    behavior: 'smooth',
  })
  const res = await requestQueue(() => sendChatApi(message))
  messagePush(res.responses)
}

async function loadWelcome() {
  try {
    const res = await requestQueue(() => getWelcomeApi())
    await messagePush(res.responses)
    await wait(2000)
  } finally {
    window.addEventListener('scroll', getHistoryMessage)
  }
}

function onSend(data: IResponseTableType) {
  data.tableResponse.sent = true
  saveMessage()
}

let scrollY: number = 0
const loadingPrev = ref(false)
function getHistoryMessage(e: Event) {
  const currentY = Math.floor(window.scrollY)
  const prevY = scrollY
  scrollY = currentY
  if (
    (prevY === 0 && e.type === 'scroll') ||
    currentY - prevY > 0 ||
    currentY > 20 ||
    loadingPrev.value
  ) {
    return
  }
  const top = document.body.offsetHeight - scrollY
  loadingPrev.value = true
  getPrevMessage()
  nextTick(() => {
    window.scrollTo(0, document.body.offsetHeight - top)
    loadingPrev.value = false
  })
}

async function handleScrollBottom() {
  await nextTick()
  const top = Math.max(0, document.body.offsetHeight - window.innerHeight + 100)
  window.scrollBy({
    top,
    behavior: 'smooth',
  })
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', getHistoryMessage)
})
loadWelcome()
</script>

<template>
  <div class="chat-page">
    <div class="header">
      <canvas ref="canvasRef" />
    </div>
    <div class="chat-panel" :style="{ marginTop: -radius + 'px' }">
      <div class="chat-panel__bg" />
      <watermark :width="width" :height="height" class="watermark" :text="waterContent" color="#e2e8ee" :font-size="16"
        :gutter-x="100" :gutter-y="80" :rotate="30" :odd-offset-x="90">
        <div class="notify-wrapper font-1">
          <div class="notify text-center">内部信息，请勿转载或分享。</div>
        </div>
        <div class="message-wrapper">
          <div class="text-center more" v-if="!isEmpty">
            <span v-if="hasHistory" @click="getHistoryMessage">点击查看历史</span>
            <span v-else>没有更多了</span>
          </div>
          <template v-for="message in messageList" :key="message.timestamp">
            <template v-if="'renderCategory' in message">
              <message-md v-if="message.renderCategory === 'MARKDOWN'" :data="message" />
              <message-table v-if="message.renderCategory === 'TABLE'" :data="message" @send="onSend(message)" />
              <message-guess v-if="message.renderCategory === 'MIXED'" :data="message" @message-click="sendMessage" />
              <message-echarts v-if="message.renderCategory === 'ECHARTS'" :data="message" />
            </template>
            <template v-else>
              <message-plain :timestamp="message.timestamp" :content="message.message" />
            </template>
          </template>
        </div>
      </watermark>
    </div>
    <div class="loading-wrapper" v-if="messageLoading">
      <span class="loading-span" v-for="i in 3" :style="{
          animationDuration: `${loadingAnimationTimeMs}ms`,
          animationDelay: `${((i - 1) * loadingAnimationTimeMs) / 4}ms`,
        }" />
    </div>
    <div class="bottom-actions">
      <ul class="quick-msg">
        <li class="text-center" @click="archiveModalRef?.open">经销商档案</li>
        <li class="text-center" @click="onInventoryClick">库存信息</li>
        <li class="text-center" @click="onFaqClick">FAQ</li>
      </ul>
      <div class="send-wrapper">
        <input type="text" placeholder="点击这里输入文字..." v-model="textMessage" @keyup.enter="handleSend" />
        <button class="send-button" @click="handleSend">
          <img src="~@/assets/images/icon-fly.png" />
        </button>
      </div>
    </div>
  </div>

  <img v-if="!isBottom" src="~@/assets/images/scroll.png"
    style="position: fixed; z-index: 1000; bottom: 7rem; right: 0; width: 4rem; height: 4rem"
    @click="handleScrollBottom" />
  <statement-modal />
  <archive-modal ref="archiveModalRef" @ok="onArchiveOk" />
</template>

<style scoped lang="scss">
.chat-page {
  $header-height: 4.533rem;
  $send-height: 2.13rem;
  $send-bottom: 1rem;
  $bottom-list-height: 1.81rem;
  $bottom-list-padding: 0.5rem;
  $bottom: calc($send-height + $send-bottom + $bottom-list-height + $bottom-list-padding * 2);
  padding-top: $header-height;
  background-color: #f8fafd;

  .more {
    height: 2rem;
    color: #999;
  }

  .header {
    position: fixed;
    width: 100%;
    top: 0;
    height: $header-height;
    z-index: 2;

    canvas {
      width: 100%;
      height: 100%;
    }
  }

  .chat-panel {
    position: relative;

    &__bg {
      background: url('@/assets/images/pic-chat-bg.png') no-repeat fixed center calc(100% - 6rem);
      background-size: contain;
      width: 100vw;
      height: 100vh;
      top: 0;
      left: 0;
      position: fixed;
      pointer-events: none;
    }
  }

  .watermark {
    padding: 3.733rem 0.8rem $bottom;
    min-height: calc(100vh - 4.533rem);
  }

  .notify-wrapper {
    position: fixed;
    padding: 0 0.5rem;
    top: calc($header-height - 0.373rem);
    z-index: 2;
  }

  .notify {
    width: 17.92rem;
    background-color: #fff7e6;
    height: 2.133rem;
    line-height: 2.133rem;
    border-radius: 0.64rem;
    color: #fa8c16;
  }

  .quick-msg {
    width: 100%;
    overflow-x: auto;
    white-space: nowrap;
    padding: $bottom-list-padding;
    display: flex;

    li {
      list-style: none;
      flex: 1;
      line-height: $bottom-list-height;
      border-radius: 2.67rem;
      border: 1px solid #efefef;

      + li {
        margin-left: 0.507rem;
      }
    }
  }

  .bottom-actions {
    width: 100%;
    position: fixed;
    bottom: 0;
    z-index: 2;
    background-color: #fff;
  }

  .send-wrapper {
    display: flex;
    padding: 0 0.5rem 1rem;

    input {
      flex: 1;
      background-color: #f8f9ff;
      border: 0;
      outline: none;
      padding: 0.5rem;
      border-radius: 0.64rem;

      &::-webkit-input-placeholder {
        color: #c2c2c2;
      }
    }

    .send-button {
      outline: none;
      margin-left: 0.5rem;
      width: $send-height;
      height: $send-height;
      background: #ffffff;
      box-shadow: 0 0.03rem 0.27rem 0 rgba(177, 192, 202, 0.4);
      border-radius: 0.64rem;
      opacity: 1;
      border: none;
      padding: 0.4rem;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }
  .loading-wrapper {
    position: fixed;
    bottom: 6.5rem;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    z-index: 999;
  }

  .loading-span {
    $size: 0.4rem;
    display: inline-block;
    width: $size;
    height: $size;
    border-radius: $size;
    background-color: #c7c7c7;
    animation-name: loading-animate;
    animation-iteration-count: infinite;

    + .loading-span {
      margin-left: 0.4rem;
    }
  }
}

@keyframes loading-animate {
  20% {
    background-color: var(--primary-color);
    transform: translateY(-0.5rem);
  }
  40% {
    background-color: #6a6a6a;
    transform: translateY(0.5rem);
  }
  50% {
    background-color: #c7c7c7;
    transform: translateY(0rem);
  }
}
</style>
